import { useEffect, useRef, useState } from 'preact/hooks'
import {
  RefreshCcw, Plus, Upload, Search, AlertCircle, MailX, AtSign, RotateCcw, Loader2,
  ChevronLeft, ChevronRight, X, CheckCircle2,
} from 'lucide-preact'
import { useSuppressions } from '@/hooks/useSuppressions'
import { useToast } from '@/hooks/useToast'
import type { SuppressionImportResultDto } from '@/types/suppressions'

interface SuppressionListProps {
  clientCompanyId: string
  /** Reports the live total up to the parent screen for the tab count. */
  onTotalChange?: (total: number) => void
}

// Format-only guard; the server is authoritative. Matches the API's syntactic check shape.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Mirror the server's per-upload cap so we fail loudly client-side instead of round-tripping a 422.
const MAX_IMPORT = 2000

// Pull every syntactically-valid, de-duplicated address out of a pasted/CSV blob. Headers and other
// columns simply don't match the pattern and are ignored — the email can sit in any column.
function extractEmails(text: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of text.split(/[\s,;]+/)) {
    const token = raw.trim().replace(/^["']|["']$/g, '')
    if (!EMAIL_PATTERN.test(token)) continue
    const key = token.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(token)
  }
  return out
}

// Human-facing label for a stored Source enum description (display only; the API value is unchanged).
function sourceLabel(source: string): string {
  switch (source) {
    case 'Webhook Complaint': return 'Spam complaint'
    case 'Admin Action': return 'Added by admin'
    case 'Unsubscribe Page': return 'Unsubscribe page'
    default: return source // 'One Click', 'Import'
  }
}

function sourceBadgeClass(source: string): string {
  switch (source) {
    case 'Webhook Complaint': return 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 ring-amber-600/20'
    case 'Import': return 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 ring-sky-600/20'
    case 'Admin Action': return 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 ring-violet-600/20'
    default: return 'text-slate-600 dark:text-slate-300 ring-slate-300/60 dark:ring-slate-600/60'
  }
}

function ImportChip({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <span class={`rounded-md bg-white/70 dark:bg-slate-900/50 px-2 py-0.5 text-xs font-medium ring-1 ring-slate-200 dark:ring-slate-700 ${muted ? 'text-slate-500 dark:text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
      {value} {label}
    </span>
  )
}

export function SuppressionList({ clientCompanyId, onTotalChange }: SuppressionListProps) {
  const {
    items, total, loading, error, setSearch, page, setPage, pageSize, reload,
    addManual, remove, importList,
  } = useSuppressions(clientCompanyId)
  const { showError, showSuccess } = useToast()

  // Keep the parent screen's tab count in sync with the live total.
  useEffect(() => { onTotalChange?.(total) }, [total, onTotalChange])

  // Debounced search box → hook search (resetting to the first page).
  const [searchInput, setSearchInput] = useState('')
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setSearch(searchInput.trim()) }, 300)
    return () => clearTimeout(t)
  }, [searchInput, setPage, setSearch])

  // Inline add form.
  const [showAddForm, setShowAddForm] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newReason, setNewReason] = useState('')
  const [submittingAdd, setSubmittingAdd] = useState(false)
  const isNewEmailValid = EMAIL_PATTERN.test(newEmail.trim())

  // Per-row + import busy state. A Set so concurrent removals each track independently (one row's
  // completion can't re-enable another row that's still in flight).
  const [pendingEmails, setPendingEmails] = useState<Set<string>>(new Set())
  const [importing, setImporting] = useState(false)
  const [importSummary, setImportSummary] = useState<{ result: SuppressionImportResultDto; fileName: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const from = total === 0 ? 0 : page * pageSize + 1
  const to = Math.min(total, (page + 1) * pageSize)
  const canPrev = page > 0
  const canNext = (page + 1) * pageSize < total

  const openAddForm = () => { setNewEmail(''); setNewReason(''); setShowAddForm(true) }

  const handleAdd = async () => {
    const trimmed = newEmail.trim()
    if (!EMAIL_PATTERN.test(trimmed)) {
      showError('Invalid email', 'Enter a valid email address.')
      return
    }
    setSubmittingAdd(true)
    const result = await addManual(trimmed, newReason)
    setSubmittingAdd(false)
    if (!result.ok) {
      if (!result.handled) showError('Could not add address', result.error || 'Unknown error')
      return
    }
    showSuccess('Address suppressed', `${trimmed} won't receive this company's marketing.`)
    setShowAddForm(false)
    setNewEmail('')
    setNewReason('')
  }

  const handleRemove = async (email: string) => {
    setPendingEmails(prev => new Set(prev).add(email))
    const result = await remove(email)
    setPendingEmails(prev => { const next = new Set(prev); next.delete(email); return next })
    if (!result.ok) {
      if (!result.handled) showError('Could not remove address', result.error || 'Unknown error')
      return
    }
    showSuccess('Address removed', `${email} can receive marketing again.`)
  }

  const handleFile = async (file: File) => {
    setImportSummary(null)
    const text = await file.text()
    const emails = extractEmails(text)
    if (emails.length === 0) {
      showError('No addresses found', 'That file had no valid email addresses.')
      return
    }
    if (emails.length > MAX_IMPORT) {
      showError('Too many addresses', `Found ${emails.length}. Up to ${MAX_IMPORT} per upload — please split the file.`)
      return
    }
    setImporting(true)
    const res = await importList(emails)
    setImporting(false)
    if (!res.ok || !res.result) {
      if (!res.handled) showError('Import failed', res.error || 'Unknown error')
      return
    }
    setImportSummary({ result: res.result, fileName: file.name })
    const updated = res.result.Reactivated + res.result.Hardened
    showSuccess('Import complete', `${res.result.Added} added${updated ? `, ${updated} updated` : ''}.`)
  }

  const onFileSelected = (e: Event) => {
    const input = e.target as HTMLInputElement
    const file = input.files?.[0]
    if (file) void handleFile(file)
    input.value = '' // allow re-selecting the same file
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-prose leading-relaxed min-w-0">
          Individual addresses excluded from this company's marketing email: unsubscribes, spam complaints,
          and opt-out lists you upload.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="hidden"
            onChange={onFileSelected}
          />
          {!showAddForm && (
            <button
              onClick={openAddForm}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" /> Add address
            </button>
          )}
        </div>
      </div>

      {/* Import result summary */}
      {importSummary && (
        <div className="rounded-xl bg-primary-50 dark:bg-primary-950/40 ring-1 ring-primary-200 dark:ring-primary-900 px-4 py-3" aria-live="polite">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 mt-0.5 text-primary-600 dark:text-primary-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-primary-800 dark:text-primary-200 break-all">
                Imported {importSummary.result.Total} addresses from {importSummary.fileName}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <ImportChip label="added" value={importSummary.result.Added} />
                <ImportChip label="re-activated" value={importSummary.result.Reactivated} />
                <ImportChip label="hardened" value={importSummary.result.Hardened} />
                <ImportChip label="already suppressed" value={importSummary.result.AlreadySuppressed} />
                <ImportChip label="invalid" value={importSummary.result.Invalid} muted />
                <ImportChip label="duplicate" value={importSummary.result.Duplicate} muted />
              </div>
            </div>
            <button
              onClick={() => setImportSummary(null)}
              aria-label="Dismiss import summary"
              className="text-primary-600/70 dark:text-primary-400/70 hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Inline add form */}
      {showAddForm && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0 space-y-1">
              <label htmlFor="new-suppression-email" className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                Email address
              </label>
              <input
                id="new-suppression-email"
                type="email"
                autoFocus
                value={newEmail}
                onInput={e => setNewEmail((e.target as HTMLInputElement).value)}
                onKeyDown={e => { if (e.key === 'Enter' && isNewEmailValid && !submittingAdd) void handleAdd() }}
                placeholder="person@example.com"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              <label htmlFor="new-suppression-reason" className="block text-xs font-medium text-slate-700 dark:text-slate-200">
                Reason <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                id="new-suppression-reason"
                type="text"
                value={newReason}
                onInput={e => setNewReason((e.target as HTMLInputElement).value)}
                placeholder="e.g. customer request"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setNewEmail(''); setNewReason('') }}
                className="px-3 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleAdd() }}
                disabled={!isNewEmailValid || submittingAdd}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {submittingAdd && <Loader2 className="w-4 h-4 animate-spin" />} Add
              </button>
            </div>
          </div>
          {newEmail.trim() && !isNewEmailValid && (
            <p className="text-xs text-red-600 dark:text-red-400">Enter a valid email address.</p>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="search"
          value={searchInput}
          onInput={e => setSearchInput((e.target as HTMLInputElement).value)}
          placeholder="Search addresses…"
          aria-label="Search suppressed addresses"
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 pl-9 pr-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="px-8 py-12 text-center border border-dashed rounded-xl border-slate-300 dark:border-slate-700">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
            <MailX className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-slate-900 dark:text-white">
            No suppressed addresses{searchInput.trim() ? ' match your search' : ' yet'}
          </h3>
          {!searchInput.trim() && (
            <>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                Add an address manually, or import a customer opt-out list to exclude people from this company's marketing.
              </p>
              <div className="mt-5 flex items-center justify-center gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <Upload className="w-4 h-4" /> Import CSV
                </button>
                <button
                  onClick={openAddForm}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4" /> Add address
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <th scope="col" className="px-4 py-3">Address</th>
                <th scope="col" className="px-4 py-3">Type</th>
                <th scope="col" className="px-4 py-3">Source</th>
                <th scope="col" className="px-4 py-3">Added</th>
                <th scope="col" className="px-4 py-3">Added by</th>
                <th scope="col" className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
              {items.map(entry => {
                const isPending = pendingEmails.has(entry.Email)
                return (
                  <tr key={entry.Id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white break-all">
                      <span className="inline-flex items-center gap-2">
                        <AtSign className="w-4 h-4 shrink-0 text-slate-400" />
                        {entry.Email}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{entry.Type}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${sourceBadgeClass(entry.Source)}`}>
                        {sourceLabel(entry.Source)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap" title={new Date(entry.CreatedUtc).toLocaleString()}>
                      {new Date(entry.CreatedUtc).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-slate-500 dark:text-slate-400 break-all">{entry.CreatedByName || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => { void handleRemove(entry.Email) }}
                        disabled={isPending}
                        aria-label={`Remove ${entry.Email}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                      >
                        {isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Remove
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between gap-4 text-sm text-slate-500 dark:text-slate-400">
          <span>{from}–{to} of {total}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => canPrev && setPage(page - 1)}
              disabled={!canPrev || loading}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <button
              onClick={() => canNext && setPage(page + 1)}
              disabled={!canNext || loading}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
